import {
  BLOCK_BADGES,
  BLOCK_LABELS,
  HORIZON_OPTIONS,
  addSeries,
  blockOutputs,
  blockSeries,
  goalLabel,
  goalSeries,
  mapTotals,
  money,
  newBlock,
  num,
  sumArr,
  type Block,
  type BlockType,
  type Goal,
  type NumericInput,
  type ValueMap,
} from "@valuemap/core";
import type { ReactNode } from "react";
import type { Dispatch } from "./EditView.js";
import styles from "./Quantify.module.css";

/**
 * The Quantify view: value blocks per goal over a 1 to 5 year horizon, with low, expected and
 * high cases. Labels, help text and outputs follow the reference tool; the numbers come from
 * `@valuemap/core`, the only implementation of the calculations (ADR-006).
 */

interface Props {
  map: ValueMap;
  canEdit: boolean;
  /** Whether value blocks can be removed (staff owners and editors). Defaults to `canEdit`. */
  canDelete?: boolean;
  dispatch: Dispatch;
}

/** Text from a number input to what is stored: a number, or "" for not entered. */
function toNumeric(text: string): NumericInput {
  const n = parseFloat(text);
  return Number.isFinite(n) ? n : "";
}

function Field({
  label,
  help,
  value,
  readOnly,
  onChange,
}: {
  label: string;
  help: string;
  value: NumericInput;
  readOnly: boolean;
  onChange: (v: NumericInput) => void;
}) {
  return (
    <label className={styles.qField}>
      <span>{label}</span>
      <input
        type="number"
        step="any"
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange(toNumeric(e.target.value))}
      />
      <span className={styles.qHelp}>{help}</span>
    </label>
  );
}

function Out({ label, value, green }: { label: string; value: string; green?: boolean }) {
  return (
    <div className={`${styles.o} ${green ? styles.green : ""}`}>
      <span className={styles.ok}>{label}</span>
      <span className={styles.ov}>{value}</span>
    </div>
  );
}

function BlockOutputs({ block, years }: { block: Block; years: number }) {
  const o = blockOutputs(block, years);
  const cells: ReactNode[] = [];
  if (o.type === "revenue" && block.type === "revenue") {
    cells.push(<Out key="base" label="Baseline revenue" value={money(o.baseline)} />);
    if (block.method === "drivers") {
      cells.push(
        <Out
          key="implied"
          label="Implied uplift"
          value={`${o.impliedUpliftPct ? o.impliedUpliftPct.toFixed(1) : "0"}%`}
        />,
      );
    }
    cells.push(<Out key="y1" label="Year 1 increase" value={money(o.year1)} green />);
    if (years > 1) {
      cells.push(<Out key="rec" label={`Recurring (yr ${years})`} value={money(o.recurring)} />);
    }
    cells.push(<Out key="tot" label={`${years}-year total`} value={money(o.total)} green />);
    if (o.low !== o.high) {
      cells.push(
        <span key="range" className={styles.note}>
          Range {money(o.low)} to {money(o.high)} over {years} years
        </span>,
      );
    }
  } else if (o.type === "efficiency" && block.type === "efficiency") {
    cells.push(<Out key="wk" label="Hours saved / week" value={num(o.hoursPerWeek, 1)} />);
    cells.push(<Out key="yr" label="Hours saved / year" value={num(o.hoursPerYear, 0)} green />);
    cells.push(<Out key="fte" label="FTE freed" value={o.fte.toFixed(2)} />);
    if (typeof block.hourlyCost === "number" && block.hourlyCost > 0) {
      cells.push(<Out key="val" label="Value / year" value={money(o.valuePerYear)} green />);
      cells.push(<Out key="tot" label={`${years}-year total`} value={money(o.total)} green />);
    } else {
      cells.push(
        <span key="note" className={styles.note}>
          Add a cost per hour to include a dollar value in the totals
        </span>,
      );
    }
  } else if (o.type === "cost") {
    cells.push(<Out key="avoid" label="Avoided / year" value={money(o.perYear)} green />);
    if (years > 1) {
      cells.push(<Out key="tot" label={`${years}-year total`} value={money(o.total)} green />);
    }
  }
  return <div className={styles.qOut}>{cells}</div>;
}

function BlockCard({
  goal,
  block,
  years,
  canEdit,
  canDelete,
  dispatch,
}: {
  goal: Goal;
  block: Block;
  years: number;
  canEdit: boolean;
  canDelete: boolean;
  dispatch: Dispatch;
}) {
  const field = (path: string) => (value: NumericInput) =>
    dispatch(
      { type: "setField", goalId: goal.id, blockId: block.id, path, value },
      { coalesce: true },
    );
  const f = (path: string, label: string, help: string, value: NumericInput): ReactNode => (
    <Field
      key={path}
      label={label}
      help={help}
      value={value}
      readOnly={!canEdit}
      onChange={field(path)}
    />
  );

  return (
    <div className={styles.qBlock}>
      <div className={styles.qBlockHead}>
        <span className={`${styles.qBadge} ${styles[block.type]}`}>{BLOCK_BADGES[block.type]}</span>
        <input
          className={styles.qBlockLabel}
          aria-label="Calculation name"
          value={block.label}
          readOnly={!canEdit}
          onChange={(e) =>
            dispatch(
              {
                type: "setField",
                goalId: goal.id,
                blockId: block.id,
                path: "label",
                value: e.target.value,
              },
              { coalesce: true },
            )
          }
        />
        {canDelete && (
          <button
            type="button"
            className={styles.qRm}
            onClick={() => dispatch({ type: "removeBlock", goalId: goal.id, blockId: block.id })}
          >
            Remove
          </button>
        )}
      </div>

      {block.type === "revenue" && (
        <>
          <div className={styles.qMethod} role="group" aria-label="How to calculate">
            {(
              [
                ["percent", "Percentage uplift"],
                ["drivers", "Build from donors"],
              ] as const
            ).map(([method, text]) => (
              <button
                key={method}
                type="button"
                className={block.method === method ? styles.on : undefined}
                aria-pressed={block.method === method}
                disabled={!canEdit}
                onClick={() =>
                  dispatch({
                    type: "setField",
                    goalId: goal.id,
                    blockId: block.id,
                    path: "method",
                    value: method,
                  })
                }
              >
                {text}
              </button>
            ))}
          </div>
          <div className={styles.qGrid}>
            {block.method === "percent" ? (
              <>
                {f(
                  "baseline",
                  "Current annual revenue ($)",
                  "What this stream brings in today, per year.",
                  block.baseline,
                )}
                {f(
                  "upliftPct",
                  "Expected uplift (%)",
                  "The recurring yearly lift, on top of today. 300 means it triples.",
                  block.upliftPct,
                )}
                {f(
                  "upliftPctLow",
                  "Low case (%)",
                  "Optional. A conservative version of the uplift.",
                  block.upliftPctLow,
                )}
                {f(
                  "upliftPctHigh",
                  "High case (%)",
                  "Optional. An optimistic version of the uplift.",
                  block.upliftPctHigh,
                )}
              </>
            ) : (
              <>
                {f(
                  "donors",
                  "Current donors (#)",
                  "How many donors give at this level today.",
                  block.donors,
                )}
                {f(
                  "avgGift",
                  "Current average gift ($)",
                  "The average amount each of them gives per year.",
                  block.avgGift,
                )}
                {f(
                  "extraDonors",
                  "Extra donors (#)",
                  "Additional donors you expect to bring in.",
                  block.extraDonors,
                )}
                {f(
                  "avgGiftIncreasePct",
                  "Average gift increase (%)",
                  "How much the average gift grows, in percent.",
                  block.avgGiftIncreasePct,
                )}
              </>
            )}
            {f(
              "ramp",
              "Realised in year 1 (%)",
              "How much of the full uplift lands in year one. 100 if it is immediate.",
              block.ramp,
            )}
            {f(
              "growthPct",
              "Compounding growth (%)",
              "Does the yearly uplift compound? 0 keeps it flat. 5 grows it 5% each year.",
              block.growthPct,
            )}
          </div>
        </>
      )}

      {block.type === "efficiency" && (
        <div className={styles.qGrid}>
          {f(
            "hoursPerWeekEach",
            "Hours / week, each person",
            "Hours one person spends on this task each week.",
            block.hoursPerWeekEach,
          )}
          {f("staff", "People doing it (#)", "How many people do this task.", block.staff)}
          {f(
            "savingPct",
            "Time saved by automating (%)",
            "Share of that time given back. 100 removes it entirely.",
            block.savingPct,
          )}
          {f(
            "weeksPerYear",
            "Weeks worked / year",
            "Working weeks per year, allowing for leave. Often 46 to 48.",
            block.weeksPerYear,
          )}
          {f(
            "hourlyCost",
            "Loaded cost / hour ($)",
            "Fully loaded staff cost per hour. Needed for a dollar value.",
            block.hourlyCost,
          )}
        </div>
      )}

      {block.type === "cost" && (
        <div className={styles.qGrid}>
          {f(
            "annualAmount",
            "Amount avoided / year ($)",
            "The cost or loss you avoid each year.",
            block.annualAmount,
          )}
          {f(
            "likelihoodPct",
            "Likelihood it occurs (%)",
            "Chance it would happen without acting. 100 if it is certain.",
            block.likelihoodPct,
          )}
          {f(
            "growthPct",
            "Compounding growth (%)",
            "Does the avoided cost compound? 0 keeps it flat. 5 grows it 5% each year.",
            block.growthPct,
          )}
        </div>
      )}

      <BlockOutputs block={block} years={years} />
    </div>
  );
}

const ADD_BLOCKS: readonly BlockType[] = ["revenue", "efficiency", "cost"];

function GoalCard({
  goal,
  index,
  years,
  canEdit,
  canDelete,
  dispatch,
}: {
  goal: Goal;
  index: number;
  years: number;
  canEdit: boolean;
  canDelete: boolean;
  dispatch: Dispatch;
}) {
  const series = goalSeries(goal, years, "exp");
  const cum = sumArr(series);
  return (
    <div className={styles.qGoal}>
      <div className={styles.qGoalHead}>
        <h2>{goalLabel(goal, index)}</h2>
        <div className={styles.qSub}>
          {cum > 0
            ? `Year 1 ${money(series[0] || 0)} · ${years}-year ${money(cum)}`
            : "Not quantified yet"}
        </div>
      </div>
      <div>
        {goal.vcase.blocks.map((b) => (
          <BlockCard
            key={b.id}
            goal={goal}
            block={b}
            years={years}
            canEdit={canEdit}
            canDelete={canDelete}
            dispatch={dispatch}
          />
        ))}
      </div>
      {canEdit && (
        <div className={styles.qAdd}>
          <span>Add calculation:</span>
          {ADD_BLOCKS.map((type) => (
            <button
              key={type}
              type="button"
              className={styles.qAddBtn}
              onClick={() => dispatch({ type: "addBlock", goalId: goal.id, block: newBlock(type) })}
            >
              {BLOCK_LABELS[type]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function QuantifyView({ map, canEdit, canDelete = canEdit, dispatch }: Props) {
  const years = map.horizon;
  const totals = mapTotals(map.goals, years);
  const cum = totals.cumulativeExpected;

  const rows = map.goals.flatMap((g, i) => {
    let s: number[] = [];
    g.vcase.blocks.forEach((b) => {
      s = addSeries(s, blockSeries(b, years, "exp"));
    });
    return sumArr(s) > 0 ? [{ goal: g, index: i, series: s }] : [];
  });

  return (
    <section className={`${styles.quantify} no-print`} aria-label="Quantify the value">
      <div className={styles.qWrap}>
        <div className={styles.qHead}>
          <div>
            <div className="eyebrow">The maths</div>
            <h1>Quantify the value</h1>
            <p className={styles.qIntro}>
              Put numbers behind each goal. Enter what you and the client believe is achievable, or
              build it from the drivers below. These are shared assumptions to pressure-test
              together, not forecasts, so set a low and high case where you can.
            </p>
          </div>
          <div className={styles.qHorizon}>
            <label htmlFor="q-years">Projection</label>
            <select
              id="q-years"
              value={years}
              disabled={!canEdit}
              onChange={(e) =>
                dispatch({ type: "setField", path: "horizon", value: parseInt(e.target.value, 10) })
              }
            >
              {HORIZON_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} year{n > 1 ? "s" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        {map.goals.length > 0 && (
          <div className={styles.qTotals} aria-live="polite">
            <div className={styles.qTotalCard}>
              <div className={styles.qtK}>Year one value</div>
              <div className={styles.qtV}>{money(totals.year1)}</div>
            </div>
            <div className={`${styles.qTotalCard} ${styles.hero}`}>
              <div className={styles.qtK}>{years}-year value</div>
              <div className={styles.qtV}>{money(cum)}</div>
              {totals.cumulativeLow !== totals.cumulativeHigh && (
                <div className={styles.qtRange}>
                  range {money(totals.cumulativeLow)} to {money(totals.cumulativeHigh)}
                </div>
              )}
            </div>
            {totals.hoursPerYear > 0 && (
              <div className={`${styles.qTotalCard} ${styles.alt}`}>
                <div className={styles.qtK}>Hours freed / year</div>
                <div className={styles.qtV}>{num(totals.hoursPerYear, 0)}</div>
              </div>
            )}
          </div>
        )}

        {map.goals.length === 0 ? (
          <div className={styles.qEmptyNote}>Add goals in Edit first, then quantify them here.</div>
        ) : (
          <>
            {map.goals.map((g, i) => (
              <GoalCard
                key={g.id}
                goal={g}
                index={i}
                years={years}
                canEdit={canEdit}
                canDelete={canDelete}
                dispatch={dispatch}
              />
            ))}
            <div className={styles.qTablewrap}>
              {cum <= 0 ? (
                <div className={styles.qEmptyNote}>
                  Enter numbers above to build the multi-year value picture.
                </div>
              ) : (
                <table className={styles.qTable}>
                  <thead>
                    <tr>
                      <th>Goal</th>
                      {Array.from({ length: years }, (_, y) => (
                        <th key={y}>Year {y + 1}</th>
                      ))}
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ goal, index, series }) => (
                      <tr key={goal.id}>
                        <td>{goalLabel(goal, index)}</td>
                        {Array.from({ length: years }, (_, y) => (
                          <td key={y}>{money(series[y] || 0)}</td>
                        ))}
                        <td>{money(sumArr(series))}</td>
                      </tr>
                    ))}
                    <tr className={styles.tot}>
                      <td>Total</td>
                      {Array.from({ length: years }, (_, y) => (
                        <td key={y}>{money(totals.series[y] || 0)}</td>
                      ))}
                      <td>{money(cum)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
